import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { money } from "@/lib/format";
import { FileText, Package, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/cuenta/")({
  component: MisPedidos,
});

const STATUS_LABELS: Record<string, string> = {
  nuevo: "Pago pendiente",
  pagado: "Pagado",
  intake_pendiente: "Falta tu formulario",
  en_proceso: "En proceso",
  esperando_cliente: "Esperando tus datos",
  completado: "Completado",
  cancelado: "Cancelado",
};

const STATUS_COLOR: Record<string, string> = {
  nuevo: "bg-muted/30 text-muted-foreground",
  pagado: "bg-success/15 text-[oklch(0.78_0.16_155)]",
  intake_pendiente: "bg-primary/15 text-primary",
  en_proceso: "bg-primary/15 text-primary",
  esperando_cliente: "bg-primary/15 text-primary",
  completado: "bg-success/15 text-[oklch(0.78_0.16_155)]",
  cancelado: "bg-destructive/15 text-destructive",
};

function MisPedidos() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("orders")
        .select("id,status,total,created_at,intake_completed,intake_token")
        .order("created_at", { ascending: false });
      setOrders(data ?? []);
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="text-muted-foreground">Cargando...</div>;

  if (orders.length === 0) {
    return (
      <div className="rounded-xl border border-hairline bg-surface p-10 text-center">
        <Package className="mx-auto h-10 w-10 text-muted-foreground" />
        <h2 className="mt-4 font-display text-xl font-semibold text-foreground">Aún no tienes pedidos</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Cuando hagas tu primera compra con este email, aparecerá aquí.
        </p>
        <Link to="/paquetes" className="mt-6 inline-block text-sm text-primary hover:underline">Ver paquetes →</Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="font-display text-lg font-semibold text-foreground">Mis pedidos</h2>
      {orders.map((o) => {
        const paid = o.status !== "nuevo";
        const needsIntake = paid && !o.intake_completed;
        return (
          <Link
            key={o.id}
            to="/cuenta/pedidos/$id"
            params={{ id: o.id }}
            className="block rounded-xl border border-hairline bg-surface p-5 transition hover:bg-surface-2"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</div>
                <div className="mt-1 font-medium text-foreground">Pedido #{o.id.slice(0, 8)}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${STATUS_COLOR[o.status] ?? "bg-muted/30"}`}>
                    {STATUS_LABELS[o.status] ?? o.status}
                  </span>
                  {needsIntake && (
                    <span className="rounded bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
                      Acción requerida
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-xl font-semibold text-foreground">{money(o.total)}</div>
                {needsIntake && o.intake_token && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({ to: "/intake/$token", params: { token: o.intake_token } });
                    }}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <FileText className="h-3 w-3" /> Completar formulario <ArrowRight className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
